import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { DeleteAuthConfirmDialog } from '@/features/admin-auth/components/DeleteAuthConfirmDialog';
import {
  useFederatedIdentities,
  useFederatedIdentityMutations,
  type FederatedIdentityViewModel,
} from '@/features/admin-federated-identities/api';
import {
  usePlatformUser,
  usePlatformUserDemographics,
  usePlatformUserMutations,
  usePlatformUserRoles,
} from '@/features/platform-users/api';
import { UserRecordView } from '@/features/user-record/UserRecordView';
import type { UserProfileDraft } from '@/features/admin-users/api';

/**
 * Global (platform-plane) user page (PTV2-1451) — the first-class view for a cluster user, at
 * /platform/users/:uuid. Feeds the shared UserRecordView the platform-plane hooks: identity summary,
 * demographics, roles, profile update, and the user's linked federated identities.
 */
export function PlatformUserDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { uuid = '' } = useParams<{ uuid: string }>();

  const userQuery = usePlatformUser(uuid);
  const rolesQuery = usePlatformUserRoles(uuid);
  const demographicsQuery = usePlatformUserDemographics(uuid);
  const { updateProfile } = usePlatformUserMutations();

  const identitiesQuery = useFederatedIdentities({ userGuid: uuid }, { enabled: uuid.length > 0 });
  const { unlink } = useFederatedIdentityMutations();
  const [unlinkTarget, setUnlinkTarget] = useState<FederatedIdentityViewModel | null>(null);

  if (userQuery.isPending) {
    return <Skeleton className="m-8 h-40 w-full max-w-xl rounded-md" />;
  }
  if (userQuery.isError) {
    return (
      <div className="max-w-xl p-8">
        <FetchErrorCallout
          error={userQuery.error}
          heading={t('userRecord.error.heading')}
          message={t('userRecord.error.message')}
          codeLabel={t('userRecord.error.codeLabel')}
          onRetry={() => {
            userQuery.refetch().catch(() => undefined);
          }}
          retryLabel={t('userRecord.error.retry')}
        />
      </div>
    );
  }

  const user = userQuery.data;
  const displayName = user.fullName.length > 0 ? user.fullName : user.name;

  function saveProfile(draft: UserProfileDraft): void {
    updateProfile.mutate({ uuid, draft });
  }

  return (
    <>
      <UserRecordView
        displayName={displayName}
        username={user.name}
        email={user.email}
        status={user.status}
        roles={rolesQuery.data ?? user.roles}
        breadcrumbs={[
          { label: t('shell.topNav.admin'), to: '/platform' },
          { label: t('routes.platform.sidebar.users'), to: '/platform/users' },
          { label: displayName },
        ]}
        demographics={demographicsQuery.data}
        demographicsLoading={demographicsQuery.isPending}
        canEdit
        saving={updateProfile.isPending}
        saveError={updateProfile.error}
        onSaveProfile={saveProfile}
        identities={{
          identities: identitiesQuery.data ?? [],
          isLoading: identitiesQuery.isPending,
          isError: identitiesQuery.isError,
          error: identitiesQuery.error,
          onRetry: () => {
            identitiesQuery.refetch().catch(() => undefined);
          },
          onUnlink: (identity) => {
            unlink.reset();
            setUnlinkTarget(identity);
          },
          pendingUnlinkGuid: unlink.isPending && unlinkTarget !== null ? unlinkTarget.guid : null,
        }}
      />

      <DeleteAuthConfirmDialog
        open={unlinkTarget !== null}
        title={t('userRecord.unlink.title')}
        message={t('userRecord.unlink.message', { identity: unlinkTarget?.externalSubject ?? '' })}
        cancelLabel={t('userRecord.unlink.cancel')}
        confirmLabel={t('userRecord.unlink.confirm')}
        onCancel={() => {
          if (unlink.isPending) return;
          setUnlinkTarget(null);
          unlink.reset();
        }}
        onConfirm={() => {
          if (unlinkTarget === null) return;
          unlink.mutate(unlinkTarget.guid, {
            onSuccess: () => {
              setUnlinkTarget(null);
              unlink.reset();
            },
          });
        }}
        pending={unlink.isPending}
        submitError={unlink.error}
        submitErrorLabel={(code) => t('userRecord.unlink.submitError', { code })}
      />
    </>
  );
}
